import React from 'react'
import { Theme, ColorTypes, useIsTheme } from '@afterpaytouch/core'
import { CONVERGENCE_NAMESPACE, NAMESPACE_SEPARATOR, THEME_COOKIE_NAME, UseCustomTranslationResponse, SetThemeRequest, LEGAL_PREFIX } from '../model'
import { useTranslation as useNextTranslation, UserConfig, SSRConfig } from 'next-i18next'
import { Trans as NextTrans } from 'next-i18next'
import { TransProps as NextTransProps } from 'react-i18next'
import { serverSideTranslations as originalServerSideTranslations } from 'next-i18next/serverSideTranslations'
import { setCookie } from 'nookies'
import { TOptions } from 'i18next'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { useCheckoutState, useCheckoutTheme, useCohort } from '../state'
import { Cohort } from '@afterpaytouch/portal-api'
import { useCallback, useEffect, useMemo } from 'react'
import { useMerchantCallback } from './merchant'

export const generateI18nNamespace = (input: string[]): string[] => {
  return input.flatMap((item) => [item, `${item}${CONVERGENCE_NAMESPACE}`])
}

/**
 * 'originalKey' is the i18n key for original Afterpay copies.
 * 'primaryKey' is the i18n key for convergence copies based on cohort, can be either AP or Cash.
 * @param key
 * @param theme
 * @param cohort
 * @param options
 * @returns
 */
export const getTranslationKeys = (key: string | string[], cohort: Cohort | undefined = Cohort.APOnly, options?: TOptions): string | string[] => {
  const processSingleKey = (originalKey: string): string | string[] => {
    if (cohort !== Cohort.CashOnly) {
      return originalKey
    }

    const keys = originalKey.split(NAMESPACE_SEPARATOR)
    if (keys.length !== 2) {
      return originalKey
    }

    const [translationNamespace, translationKey] = keys

    const primaryKey = `${translationNamespace}${CONVERGENCE_NAMESPACE}:${translationKey}`

    // There is a weird bug where if there are two keys and returnObjects is true, for eg
    // ['terminalError__cash:amountTooHigh.option', 'terminalError:amountTooHigh.option']
    // It won't use the __cash version - but the fallback one.
    if (options?.returnObjects) {
      return [primaryKey]
    }

    return [primaryKey, originalKey]
  }

  if (Array.isArray(key)) {
    const processedKeys = key.reduce<Array<string | string[]>>((acc, cur) => {
      const processed = processSingleKey(cur)
      return Array.isArray(processed) ? [...acc, ...processed] : [...acc, processed]
    }, [])
    return processedKeys.flat()
  } else {
    return processSingleKey(key)
  }
}

// Note: this custom useTranslation hook generate additional translation key for convergence
// If you need to use diff copy for Cohort.CashOnly, for eg: summary__cash.json, please use this hook
// If the options contain { legal: true }, the key will be prefixed with |░legal░| to find the legal translation

export const useTranslation = (ns?: string[]): UseCustomTranslationResponse => {
  const cohort = useCohort()
  const { t: originalTFunction, i18n, ready } = useNextTranslation(ns)

  const enrichedT = useCallback(
    ((originalKey: string | string[], ...rest: unknown[]) => {
      const maybeOptions = rest.length > 0 ? rest[rest.length - 1] : undefined
      const options = typeof maybeOptions === 'object' && maybeOptions !== null ? (maybeOptions as TOptions & { legal?: boolean }) : undefined

      // If legal: true is set, prepend the legal prefix to the key(s)
      const applyLegalPrefix = (key: string): string => (options?.legal ? `${LEGAL_PREFIX}${key}` : key)
      const keyWithLegalPrefix = Array.isArray(originalKey) ? originalKey.map(applyLegalPrefix) : applyLegalPrefix(originalKey)

      const convergenceKeys = getTranslationKeys(keyWithLegalPrefix, cohort, options)
      return (originalTFunction as unknown as (...args: unknown[]) => unknown)(convergenceKeys, ...rest)
    }) as typeof originalTFunction,
    [cohort, originalTFunction]
  )

  return useMemo(
    () => ({
      t: enrichedT,
      i18n,
      ready,
    }),
    [enrichedT, i18n, ready]
  )
}

export const serverSideTranslations = async (
  initialLocale: string,
  namespacesRequired: string[],
  configOverride?: UserConfig | null,
  extraLocales?: string[] | false
): Promise<SSRConfig> => {
  const convergenceNameSpaces = generateI18nNamespace(namespacesRequired)
  return await originalServerSideTranslations(initialLocale, convergenceNameSpaces, configOverride, extraLocales)
}

export const updateTheme = async (data: SetThemeRequest): Promise<void> => {
  try {
    setCookie(null, THEME_COOKIE_NAME, data.theme, {
      // maxAge: 30 * 24 * 60 * 60, // 30 days
      // maxAge: 6 * 60 * 60, // 6 hours
      path: '/',
      // httpOnly: true, // cater for payment schedule widget
      secure: true,
      sameSite: 'none',
      // domain // cater for payment schedule widget
    })
  } catch (error) {
    console.error('Failed to set theme')
  }
}

export const isValidThemeValue = (value?: string): value is Theme => !isUndefinedOrEmpty(value) && Object.values(Theme).includes(value as Theme)

/**
 * Finds the closest shared parent domain between two hosts (e.g. portal.afterpay.com
 * and credit-application.afterpay.com share afterpay.com). Returns undefined when the
 * hosts are identical (host-only cookie suffices) or share fewer than two labels, so a
 * cookie is never scoped to a bare TLD.
 */
export const getSharedCookieDomain = (currentHost: string, targetHost: string): string | undefined => {
  if (currentHost === targetHost) {
    return undefined
  }

  const current = currentHost.split('.')
  const target = targetHost.split('.')
  const shared: string[] = []

  while (current.length > 0 && target.length > 0 && current[current.length - 1] === target[target.length - 1]) {
    shared.unshift(current.pop() as string)
    target.pop()
  }

  return shared.length >= 2 ? shared.join('.') : undefined
}

/**
 * Pre-configures the theme for another Afterpay app before redirecting to it.
 * Sibling apps (e.g. credit-application) are passive theme consumers: they read the
 * `x-apt-theme` cookie and fall back to their own default when it is absent. Since the
 * apps live on different subdomains, the cookie is scoped to the closest shared parent
 * domain so the target app can read it.
 */
export const setThemeHandoffCookie = (theme: Theme, targetUrl: string): void => {
  try {
    const domain = getSharedCookieDomain(window.location.hostname, new URL(targetUrl).hostname)
    setCookie(null, THEME_COOKIE_NAME, theme, {
      path: '/',
      secure: true,
      sameSite: 'none',
      ...(domain !== undefined && { domain }),
    })
  } catch (error) {
    console.error('Failed to set theme handoff cookie')
  }
}

export const useSubHeadingColor = (): ColorTypes => {
  const isAPTheme = useIsTheme(Theme.AP)

  return isAPTheme ? 'Black' : 'Gray40'
}

export const isValidCohortValue = (value?: string): value is Cohort => !isUndefinedOrEmpty(value) && Object.values(Cohort).includes(value as Cohort)

export const useUpdateAfterpayJsBrand = (): void => {
  const [theme] = useCheckoutTheme()
  const { initial } = useCheckoutState()
  const { updateBrand } = useMerchantCallback()

  useEffect(() => {
    if (initial.isWindowed) {
      updateBrand(theme)?.catch(() => {})
    }
  }, [theme])
}

/**
 *  Determines if a consumer uuid is a shadow account or not.
 *  @param {string} consumerUuid
 *  @returns {boolean} Returns `true` if is a shadow account.
 */

export const isShadowAccount = (consumerUuid: string): boolean => {
  return consumerUuid.startsWith('C_')
}

type TransProps = Omit<NextTransProps<string>, 'i18nKey' | 'context' | 'defaults' | 'tOptions'> & {
  i18nKey: string | string[]
  context?: string
  defaults?: string
  tOptions?: (TOptions & { legal?: boolean }) | Record<string, unknown>
}

export const Trans = ({ i18nKey, tOptions, ...otherProps }: TransProps): React.ReactElement => {
  const cohort = useCohort()
  const isLegal = typeof tOptions === 'object' && tOptions !== null && 'legal' in tOptions && tOptions.legal === true

  // If legal: true is set, prepend the legal prefix to the key(s)
  const applyLegalPrefix = (key: string): string => (isLegal ? `${LEGAL_PREFIX}${key}` : key)
  const keyWithLegalPrefix = Array.isArray(i18nKey) ? i18nKey.map(applyLegalPrefix) : applyLegalPrefix(i18nKey)

  const convergenceKeys = getTranslationKeys(keyWithLegalPrefix, cohort)

  return <NextTrans {...otherProps} i18nKey={convergenceKeys} tOptions={tOptions} />
}
